import React from "react";
import { AlertTriangle } from "lucide-react";

export function ConfirmModal({
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Delete",
  variant = "danger",
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  variant?: "danger" | "primary";
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="bg-surface-container-lowest border border-outline-variant rounded-xl shadow-xl p-xl max-w-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-md mb-lg">
          <div
            className={`p-2 rounded-full shrink-0 ${variant === "danger" ? "bg-error-container text-error" : "bg-primary/10 text-primary"}`}
          >
            <AlertTriangle size={20} />
          </div>
          <p className="font-body-md text-on-surface pt-1">{message}</p>
        </div>
        <div className="flex justify-end gap-sm">
          <button
            onClick={onCancel}
            className="px-5 py-2.5 text-secondary font-label-sm hover:bg-surface-container rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-5 py-2.5 font-label-sm rounded-lg transition-opacity hover:opacity-90 ${variant === "danger" ? "bg-error text-on-error" : "bg-primary text-on-primary"}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PromptModal({
  title,
  placeholder,
  onSubmit,
  onCancel,
}: {
  title: string;
  placeholder?: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [value, setValue] = React.useState("");

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim()) onSubmit(value.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="bg-surface-container-lowest border border-outline-variant rounded-xl shadow-xl p-xl max-w-2xl w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-h3 text-primary mb-lg">{title}</h3>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-surface-container border border-outline-variant rounded-lg px-3 py-2.5 font-body-md text-primary placeholder:text-outline focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors mb-lg"
          />
          <div className="flex justify-end gap-sm">
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 text-secondary font-label-sm hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!value.trim()}
              className="px-5 py-2.5 bg-primary text-on-primary font-label-sm rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
